import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {convertStrandToDisplayName, CustomField, Strand} from 'constants/MolbioConstants';
import {AnnotationData} from 'molbio/annotations/AnnotationUtil';
import FeatureTableChipList from 'molbio/sequence-features/FeatureTableChipList';
import VisibilityToggle from 'molbio/sequence-features/VisibilityToggle';
import {ChipSize} from 'ui-library/chips/Chip';
import {getChipsRowHeight} from 'ui-library/chips/ChipList';
import {ColorSwatch} from 'ui-library/ColorSwatch';
import {
  RowSelectionMode,
  ScrollableTableColumn,
  ScrollableTableRow,
  TABLE_KEYS,
  TABLE_ROW_BODY_HEIGHT,
} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';

type AnnotationTableSortKey =
  | 'name'
  | 'type'
  | 'start'
  | 'end'
  | 'length'
  | 'strandSort'
  | 'color'
  | 'notesSort';

interface AnnotationTableColumn extends ScrollableTableColumn {
  accessorKey: keyof AnnotationRowData;
  sortKey?: AnnotationTableSortKey;
}

const ANNOTATION_COLUMNS: Array<AnnotationTableColumn> = [
  {
    header: 'Name',
    accessorKey: 'nameElement',
    isSortable: true,
    sortKey: 'name',
  },
  {
    header: 'Type',
    accessorKey: 'type',
    isSortable: true,
    sortKey: 'type',
  },
  {
    header: 'Start',
    accessorKey: 'start',
    isSortable: true,
    sortKey: 'start',
  },
  {
    header: 'End',
    accessorKey: 'end',
    isSortable: true,
    sortKey: 'end',
  },
  {
    header: 'Length',
    accessorKey: 'length',
    isSortable: true,
    sortKey: 'length',
  },
  {
    header: 'Strand',
    accessorKey: 'strand',
    isSortable: true,
    sortKey: 'strandSort',
  },
  {
    header: 'Color',
    accessorKey: 'colorElement',
    isSortable: true,
    sortKey: 'color',
  },
  {
    header: 'Notes',
    accessorKey: 'notes',
    isSortable: true,
    sortKey: 'notesSort',
  },
  {
    header: 'Custom fields',
    accessorKey: 'customFieldsElement',
    isSortable: false,
    width: 300,
  },
];

interface AnnotationRowData {
  id: string;
  name: string;
  nameElement: JSX.Element;
  type: string;
  start: number;
  end: number;
  length: number;
  strand: string;
  strandSort: Strand;
  color: string;
  colorElement: JSX.Element;
  notes: JSX.Element;
  notesSort: string;
  customFieldsElement: JSX.Element;
  customFields: Array<CustomField>;
}

interface AnnotationRow extends ScrollableTableRow {
  data: AnnotationRowData;
}

function VisibilityAndNameCell({
  name,
  isVisible,
  onChangeVisibility,
}: {
  name: string;
  isVisible: boolean;
  onChangeVisibility: () => void;
}): JSX.Element {
  return (
    <div className={css(styles.nameCell)}>
      <VisibilityToggle isVisible={isVisible} onClick={onChangeVisibility} />
      <p className={css(styles.nameContents)}>{name}</p>
    </div>
  );
}

/**
 * Converts an annotation into the correct format for a table row.
 * @param annotationData Annotation data.
 * @param selectedIds Ids currently selected in the table.
 * @param onToggleVisibility Callback to set visibility for an array of annotations.
 */
export function getAnnotationRow({
  annotationData,
  selectedIds,
  onToggleVisibility,
}: {
  annotationData: AnnotationData;
  selectedIds: Array<string>;
  onToggleVisibility: (cids: Array<string>, visible: boolean) => void;
}): AnnotationRow {
  return {
    id: annotationData.cid,
    isSelected: selectedIds.includes(annotationData.cid),
    data: {
      id: annotationData.cid,
      name: annotationData.name,
      nameElement: (
        <VisibilityAndNameCell
          name={annotationData.name}
          isVisible={annotationData.visible}
          onChangeVisibility={() => {
            onToggleVisibility([annotationData.cid], !annotationData.visible);
          }}
        />
      ),
      type: annotationData.annotation_type,
      start: annotationData.displayStart,
      end: annotationData.displayEnd,
      length: annotationData.length,
      strand: convertStrandToDisplayName(annotationData.strand),
      strandSort: annotationData.strand,
      color: annotationData.color,
      colorElement: <ColorSwatch fillColor={annotationData.color} darkerBorder={false} small={true} />,
      notes: <div className={css(styles.notesCell)}>{annotationData.notes}</div>,
      notesSort: annotationData.notes,
      customFieldsElement: (
        <FeatureTableChipList
          items={annotationData.custom_fields.map(({name, value}) => `${name}: ${value}`)}
        />
      ),
      customFields: annotationData.custom_fields,
    },
  };
}

export interface AnnotationTableProps {
  annotations: Array<AnnotationData>;
  selectedIds: Array<string>;
  setSelectedIds: (ids: Array<string>) => void;
  allowFeatureEdits: boolean;
  onToggleVisibility: (cids: Array<string>, visible: boolean) => void;
}

/**
 * Renders a selectable table of annotations.
 * @param annotations List of annotations to display.
 * @param setSelectedIds Callback to update the set of selected ids.
 * @param allowFeatureEdits Whether features are editable. Determines if features are selectable.
 * @param selectedIds Ids currently selected in the table.
 * @param onToggleVisibility Callback to set visibility for an array of annotations.
 */
export default function AnnotationTable({
  annotations,
  selectedIds,
  setSelectedIds,
  allowFeatureEdits,
  onToggleVisibility,
}: AnnotationTableProps): JSX.Element {
  function handleSelection(toggledRows: Array<AnnotationRow>, isSelected: boolean): void {
    const toggledIds = toggledRows.map((r: AnnotationRow) => r.id);
    if (isSelected) {
      const newlySelected = toggledIds.filter((id) => !selectedIds.includes(id));
      setSelectedIds([...selectedIds, ...newlySelected]);
    } else {
      setSelectedIds(selectedIds.filter((id) => !toggledIds.includes(id)));
    }
  }

  const rows = annotations.map((annotationData) =>
    getAnnotationRow({
      annotationData,
      selectedIds,
      onToggleVisibility,
    })
  );

  function getRowHeight(rowIdx: number): number {
    if (rowIdx >= rows.length) {
      return TABLE_ROW_BODY_HEIGHT;
    }
    return getChipsRowHeight(rows[rowIdx].data.customFields.length, ChipSize.TABLE_CELL);
  }

  return (
    <SortableScrollableTable
      className={css(styles.table)}
      canResizeColumns={true}
      isCompact={true}
      columns={ANNOTATION_COLUMNS}
      emptyTableText="This sequence has no annotations"
      initialRows={rows}
      initialSort={[{sortKey: 'start'}]}
      getSortValue={(row: AnnotationRow, sortKey: AnnotationTableSortKey) => row.data[sortKey]}
      tableKey={TABLE_KEYS.EXPANDED_ANNOTATION_TABLE}
      rowSelectionMode={RowSelectionMode.MULTIPLE}
      disableSelection={!allowFeatureEdits}
      onRowsSelect={handleSelection}
      matchParentHeight={true}
      getRowContentHeight={getRowHeight}
    />
  );
}

const styles = StyleSheet.create({
  table: {
    height: '100%',
  },
  nameCell: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  nameContents: {
    marginLeft: '8px',
    marginBottom: '0',
  },
  notesCell: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});
